<template>
  <div class="flex justify-between items-center mt-2.5">
    <div class="inline-flex gap-1.5 items-center">
      <span class="text-gray-600 text-sm font-medium">Showing (<span class="text-gray-900 !font-medium">{{ itemsShowing }}</span>) of <span class="text-gray-900 !font-medium">{{ totalItems }}</span> Results</span>
      <template v-if="totalItems > 10">
        <span class="pl-3">|</span>
        <PaginationDropdown v-model="itemsPerPage" />
      </template>
    </div>

    <div
      v-if="totalPages > 1"
      class=""
    >
      <ul class="pagination">
        <!-- First page -->
        <template v-if="includeFirstAndLastPageLinks">
          <li>
            <a
              class="first"
              :class="{ disabled: isFirstPage }"
              @click="goToPage(1)"
            >
              <!--              <i class="fa-regular fa-chevron-double-left" />-->
              First
            </a>
          </li>
        </template>

        <!-- Previous page -->
        <li>
          <a
            class="previous"
            :class="[
              { disabled: isFirstPage },
              includeFirstAndLastPageLinks ? 'previous' : 'first !border-l'
            ]"
            @click="!isFirstPage && goToPage(currentPage - 1)"
          >
            Previous
          </a>
        </li>

        <li class="pagination-input">
          <span>Page</span>
          <input
            :value="currentPage"
            :class="{'is-invalid': hasError}"
            type="text"
            @keydown.enter.stop="handleEnter"
            @keydown.stop="handleInput"
          >
          <span>of {{ totalPages }}</span>
        </li>

        <!-- Next page -->
        <li

          class="page-item"
        >
          <a
            class="next"
            :class="[
              { disabled: isLastPage },
              includeFirstAndLastPageLinks ? 'next' : 'last'
            ]"
            @click="!isLastPage && goToPage(currentPage + 1)"
          >
            Next
          </a>
        </li>

        <!-- Last page -->
        <template v-if="includeFirstAndLastPageLinks">
          <li>
            <a
              class="last"
              :class="{ disabled: isLastPage }"
              @click="goToPage(totalPages)"
            >
              Last
              <!--              <i class="fa-regular fa-chevron-double-right" />-->
            </a>
          </li>
        </template>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { PaginationDropdown } from '@/components/dropdowns/index.js';

const itemsPerPage = defineModel('itemsPerPage', { type: Number, default: 10 });
const currentPage = defineModel('currentPage', { type: Number, default: 1 });

const props = defineProps ({
  totalItems: {
    type: Number,
    default: 0,
  },
  includeFirstAndLastPageLinks: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits(['page-change', 'items-per-page-change']);

const totalPages = computed(() => (props.totalItems && itemsPerPage.value) ? Math.ceil(props.totalItems / itemsPerPage.value) : 0);
const isFirstPage = computed(() => currentPage.value === 1);
const isLastPage = computed(() => {
  return currentPage.value === totalPages.value;
});

const internalPageNumber = ref();

const hasError = ref(false);

const itemsShowing = computed(() => {
  if (props.totalItems === 0) return '0';

  const start = (currentPage.value - 1) * itemsPerPage.value + 1;
  const end = Math.min(currentPage.value * itemsPerPage.value, props.totalItems);

  return `${start} - ${end}`;
});

const handleEnter = (event) => {
  const input = event.target.value;

  if ((input >= 1) && (input <= totalPages.value)) {
    if (input !== currentPage.value.toString()) {
      hasError.value = false;
      goToPage(input);
    }
  }
  else {
    hasError.value = true;
  }

};

const handleInput = event => {
  if (event.key.length === 1 && isNaN(Number(event.key))) {
    event.preventDefault();
    event.stopPropagation();
  }
};

const goToPage = page => {
  internalPageNumber.value = page;
  currentPage.value = page;
  emit('page-change', page);
};

watch(itemsPerPage, newVal => {
  emit('items-per-page-change', newVal);

  // Reset to the first page
  if (currentPage.value !== 1) {
    goToPage(1);
  }
});

</script>

<style lang="css" scoped>
a {
  cursor: pointer;
}
</style>
